/**
 * PageSummary — collapsible AI-generated summary card displayed above page content.
 * Fetches the page annotation and shows the L1 summary text + key themes.
 * Hidden if no annotation exists (page not yet processed by AI pipeline).
 */

import React, { useEffect, useState } from 'react';
import * as pagesApi from '../../api/pages-api';
import type { PageAnnotationData } from '../../api/pages-api';

interface PageSummaryProps {
  readonly pageId: string;
}

export function PageSummary({ pageId }: PageSummaryProps): React.ReactElement | null {
  const [annotation, setAnnotation] = useState<PageAnnotationData | null>(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function load(): Promise<void> {
      setLoading(true);
      try {
        const response = await pagesApi.getPageAnnotation(pageId);
        if (!cancelled) {
          setAnnotation(response.data);
        }
      } catch {
        if (!cancelled) {
          setAnnotation(null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    load();
    return () => { cancelled = true; };
  }, [pageId]);

  // Don't render anything while loading or if no summary exists
  if (loading) return null;
  if (!annotation?.summary) return null;

  return (
    <div className="mb-4 rounded-lg border bg-accent/20 overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-4 py-2.5 text-left hover:bg-accent/30 transition-colors"
        aria-expanded={expanded}
        aria-controls="page-summary-content"
      >
        <div className="flex items-center gap-2">
          <svg className="h-4 w-4 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
          </svg>
          <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            AI Summary
          </span>
        </div>
        <svg
          className={`h-4 w-4 text-muted-foreground transition-transform ${expanded ? 'rotate-180' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          aria-hidden="true"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {expanded && (
        <div id="page-summary-content" className="px-4 pb-3 space-y-2">
          <p className="text-sm text-foreground leading-relaxed">
            {annotation.summary.text}
          </p>

          {annotation.summary.keyThemes.length > 0 && (
            <div className="flex flex-wrap gap-1.5 pt-1">
              {annotation.summary.keyThemes.map((theme) => (
                <span
                  key={theme}
                  className="inline-block px-2 py-0.5 text-xs rounded-full bg-primary/10 text-primary"
                >
                  {theme}
                </span>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
